<script setup lang="ts">
import { computed, ref, useId } from "vue";
import type { GeneratedProperty } from "@/composables/generated/contracts";
import { enumItems, fieldKind } from "@/composables/generated/fields";

const props = defineProps<{
  meta: GeneratedProperty;
  label: string;
  modelValue: unknown;
  readonly?: boolean;
  disabled?: boolean;
  error?: string;
  density?: "default" | "comfortable" | "compact";
}>();
const emit = defineEmits<{ "update:modelValue": [value: unknown] }>();
const controlId = useId();
const kind = computed(() => fieldKind(props.meta));
const choices = computed(() => enumItems(props.meta));
const fieldLabel = computed(
  () => `${props.label}${props.meta.required ? " *" : ""}`,
);
const booleanItems = [
  { title: "Yes", value: true },
  { title: "No", value: false },
];
const formatError = ref("");
const errorMessages = computed(() =>
  [props.error, formatError.value].filter((message): message is string =>
    Boolean(message),
  ),
);
const isDateKind = computed(
  () => kind.value === "date" || kind.value === "datetime",
);
const usesTextField = computed(
  () =>
    kind.value !== "custom" &&
    kind.value !== "enum" &&
    !(kind.value === "boolean" && !props.meta.isArray) &&
    !(props.meta.isArray && (kind.value === "boolean" || isDateKind.value)),
);
const inputType = computed(() =>
  props.meta.isArray
    ? "text"
    : kind.value === "date"
      ? "date"
      : kind.value === "datetime"
        ? "datetime-local"
        : kind.value === "number"
          ? "number"
          : "text",
);
const selectValue = computed<(string | number)[] | string | number | null>(
  () => {
    const value = props.modelValue;
    if (props.meta.isArray)
      return Array.isArray(value)
        ? value.filter(
            (item): item is string | number =>
              typeof item === "string" || typeof item === "number",
          )
        : [];
    return typeof value === "string" || typeof value === "number"
      ? value
      : null;
  },
);
const booleanValue = computed(() =>
  typeof props.modelValue === "boolean" ? props.modelValue : null,
);
const inputValue = computed(() => {
  const value = props.modelValue;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return "";
    const local = new Date(value.getTime() - value.getTimezoneOffset() * 60000);
    return local.toISOString().slice(0, kind.value === "date" ? 10 : 16);
  }
  if (Array.isArray(value)) return value.join(", ");
  return typeof value === "string" || typeof value === "number"
    ? String(value)
    : "";
});
function updateSelection(value: unknown) {
  if (props.meta.isArray) emit("update:modelValue", value ?? []);
  else emit("update:modelValue", value ?? null);
}
function updateText(raw: string | null) {
  const text = raw ?? "";
  let value: unknown = text;
  if (text === "") value = null;
  else if (props.meta.isArray) {
    const values = text.split(",").map((part) => part.trim());
    value = kind.value === "number" ? values.map(Number) : values;
    if (
      Array.isArray(value) &&
      value.some((item) => typeof item === "number" && !Number.isFinite(item))
    ) {
      formatError.value = "Enter comma-separated numbers.";
      return;
    }
  } else if (kind.value === "number") value = Number(text);
  else if (kind.value === "date") {
    // "YYYY-MM-DD" parses as UTC midnight; build local midnight so the day doesn't shift.
    const [year, month, day] = text.split("-").map(Number);
    value = new Date(year, month - 1, day);
  } else if (kind.value === "datetime") value = new Date(text);
  formatError.value = "";
  emit("update:modelValue", value);
}
</script>

<template>
  <div class="generated-field">
    <v-select
      v-if="kind === 'enum'"
      :id="controlId"
      :label="fieldLabel"
      :items="choices"
      :model-value="selectValue"
      :multiple="meta.isArray"
      :chips="meta.isArray"
      :clearable="!meta.required && !readonly"
      :readonly="readonly"
      :disabled="disabled"
      :error-messages="errorMessages"
      :density="density"
      :hide-details="density === 'compact' ? 'auto' : undefined"
      @update:model-value="updateSelection"
    />
    <v-select
      v-else-if="kind === 'boolean' && !meta.isArray"
      :id="controlId"
      :label="fieldLabel"
      :items="booleanItems"
      :model-value="booleanValue"
      :clearable="!meta.required && !readonly"
      :readonly="readonly"
      :disabled="disabled"
      :error-messages="errorMessages"
      :density="density"
      :hide-details="density === 'compact' ? 'auto' : undefined"
      @update:model-value="updateSelection"
    />
    <v-text-field
      v-else-if="usesTextField"
      :id="controlId"
      :label="fieldLabel"
      :type="inputType"
      step="any"
      :model-value="inputValue"
      :readonly="readonly"
      :disabled="disabled"
      :error-messages="errorMessages"
      :density="density"
      :hide-details="density === 'compact' ? 'auto' : undefined"
      :hint="meta.isArray ? 'Comma-separated values' : undefined"
      :persistent-hint="meta.isArray"
      @update:model-value="updateText"
    />
    <v-alert v-else type="warning" variant="tonal" role="alert">
      {{ fieldLabel }}: provide a field slot to select this relation or complex
      value.
    </v-alert>
  </div>
</template>

<style scoped>
.generated-field {
  margin-bottom: 4px;
}
</style>
